import React from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer, LEGAL_ENTITY } from '../components/Footer';

const TermsPage = () => {
  const scrollToCompare = () => {
    window.location.href = '/compare';
  };

  const lastUpdated = 'January 2026';

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header onStartComparing={scrollToCompare} />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-gray-50 to-orange-50/30 py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms & Conditions</h1>
            <p className="text-gray-600">Last updated: {lastUpdated}</p>
          </div>
        </section>

        {/* Content */}
        <section className="py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="prose prose-gray max-w-none">
              
              {/* Introduction */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
                <p className="text-gray-600 mb-4">
                  Welcome to {LEGAL_ENTITY.website}. These Terms & Conditions ("Terms") govern your use of our 
                  website and services. By accessing or using our website, you agree to be bound by these Terms.
                </p>
                <p className="text-gray-600 mb-4">
                  {LEGAL_ENTITY.website} is owned and operated by <strong>{LEGAL_ENTITY.name}</strong> (ABN {LEGAL_ENTITY.abn}) 
                  ("we", "us", "our"). References to "we", "us", or "our" throughout these Terms refer to {LEGAL_ENTITY.name}.
                </p>
              </div>

              {/* Services */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Our Services</h2>
                <p className="text-gray-600 mb-4">
                  We provide a free online comparison platform that allows users to compare home energy solutions, 
                  including but not limited to:
                </p>
                <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                  <li>Solar systems</li>
                  <li>Battery storage</li>
                  <li>EV chargers</li>
                  <li>Heat pumps</li>
                  <li>Heating and cooling systems</li>
                </ul>
                <p className="text-gray-600">
                  Our platform provides general comparison information and indicative pricing to help you make 
                  informed decisions. All comparisons and information are provided by {LEGAL_ENTITY.name}.
                </p>
              </div>

              {/* Disclaimer */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Disclaimer</h2>
                <p className="text-gray-600 mb-4">
                  The information provided on our website is for general comparison purposes only and does not 
                  constitute:
                </p>
                <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                  <li>Financial advice</li>
                  <li>Legal advice</li>
                  <li>Professional advice of any kind</li>
                  <li>A recommendation to purchase any particular product or service</li>
                </ul>
                <p className="text-gray-600 mb-4">
                  All pricing information displayed on our website is indicative only and may vary based on 
                  individual circumstances, location, installation requirements, and other factors. Actual 
                  prices may differ from those displayed.
                </p>
                <p className="text-gray-600">
                  We recommend that you seek independent professional advice before making any decisions 
                  regarding your home energy solutions.
                </p>
              </div>

              {/* User Responsibilities */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">4. User Responsibilities</h2>
                <p className="text-gray-600 mb-4">When using our website, you agree to:</p>
                <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                  <li>Provide accurate and truthful information</li>
                  <li>Use the website for lawful purposes only</li>
                  <li>Not attempt to interfere with the proper functioning of the website</li>
                  <li>Not use automated systems to access the website without our permission</li>
                  <li>Not misrepresent your identity or affiliation</li>
                </ul>
              </div>

              {/* Privacy */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Privacy</h2>
                <p className="text-gray-600 mb-4">
                  Your privacy is important to us. Our collection and use of personal information is governed 
                  by our <Link to="/privacy" className="text-cyo-orange hover:underline">Privacy Policy</Link>, 
                  which forms part of these Terms.
                </p>
                <p className="text-gray-600">
                  By using our website and submitting your information, you consent to the collection, use, 
                  and disclosure of your information as described in our Privacy Policy.
                </p>
              </div>

              {/* Intellectual Property */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Intellectual Property</h2>
                <p className="text-gray-600 mb-4">
                  All content on our website, including text, graphics, logos, images, and software, is the 
                  property of {LEGAL_ENTITY.name} or its licensors and is protected by Australian and 
                  international copyright laws.
                </p>
                <p className="text-gray-600">
                  You may not reproduce, distribute, modify, or create derivative works from any content on 
                  our website without our prior written consent.
                </p>
              </div>

              {/* Limitation of Liability */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Limitation of Liability</h2>
                <p className="text-gray-600 mb-4">
                  To the maximum extent permitted by law, {LEGAL_ENTITY.name} shall not be liable for any 
                  indirect, incidental, special, consequential, or punitive damages arising out of or 
                  related to your use of our website or services.
                </p>
                <p className="text-gray-600">
                  We do not guarantee the accuracy, completeness, or reliability of any information on our 
                  website and shall not be liable for any decisions made based on such information.
                </p>
              </div>

              {/* Third Party Links */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Third Party Links</h2>
                <p className="text-gray-600">
                  Our website may contain links to third-party websites. We are not responsible for the 
                  content, privacy practices, or terms of use of any third-party websites. Your use of 
                  such websites is at your own risk.
                </p>
              </div>

              {/* Changes to Terms */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Changes to These Terms</h2>
                <p className="text-gray-600">
                  We reserve the right to modify these Terms at any time. Changes will be effective 
                  immediately upon posting on our website. Your continued use of our website after any 
                  changes indicates your acceptance of the modified Terms.
                </p>
              </div>

              {/* Governing Law */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Governing Law</h2>
                <p className="text-gray-600">
                  These Terms are governed by and construed in accordance with the laws of Australia. 
                  Any disputes arising from these Terms or your use of our website shall be subject to 
                  the exclusive jurisdiction of the courts of Australia.
                </p>
              </div>

              {/* Contact */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Contact Us</h2>
                <p className="text-gray-600 mb-4">
                  If you have any questions about these Terms, please contact us at:
                </p>
                <div className="bg-gray-50 rounded-xl p-6">
                  <p className="text-gray-700 font-medium">{LEGAL_ENTITY.name}</p>
                  <p className="text-gray-600">ABN: {LEGAL_ENTITY.abn}</p>
                  <p className="text-gray-600">Email: hello@compareyouroptions.com.au</p>
                  <p className="text-gray-600">Website: {LEGAL_ENTITY.website}</p>
                </div>
              </div>

            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default TermsPage;
